import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Cylinder, KanbanSquare, Plus, LogOut, User, ChevronDown,
  FileSearch, FileText, ClipboardList, FileCheck2, Wrench, Factory, Truck, Upload,
  KeyRound, ShieldAlert, BarChart3,
} from "lucide-react";import { useAuth, ROLE_LABELS, canCreateCylinder } from "@/contexts/AuthContext";
import GlobalSearch from "@/components/GlobalSearch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAVY = "#0B1F3A";
const RED  = "#C62828";
const GREEN = "#2E7D32";

// Permission-driven navigation. Every module has a stable key that maps to the
// backend permission matrix (see /app/backend/permissions.py). Sidebar entries
// are hidden automatically when the department loses `view` on that module.
const COMMERCIAL_NAV = [
  { to: "/inquiries", label: "Inquiries", icon: FileSearch, testid: "nav-inquiries", module: "inquiries" },
  { to: "/quotations", label: "Quotations", icon: FileText, testid: "nav-quotations", module: "quotations" },
  { to: "/purchase-orders", label: "Customer POs", icon: ClipboardList, testid: "nav-pos", module: "purchase_orders" },
  { to: "/order-acceptances", label: "Order Acceptances", icon: FileCheck2, testid: "nav-oas", module: "order_acceptances" },
  { to: "/receiving", label: "Receiving (DC)", icon: Truck, testid: "nav-receiving", module: "receiving" },
];

const MFG_NAV = [
  { to: "/engineering", label: "Design Queue", icon: Wrench, testid: "nav-engineering", module: "engineering" },
  { to: "/lifecycle", label: "Lifecycle Tracker", icon: Factory, testid: "nav-lifecycle", module: "lifecycle" },
  { to: "/cylinders", label: "Cylinders", icon: Cylinder, testid: "nav-cylinders", module: "cylinders" },
  { to: "/kanban", label: "Kanban Board", icon: KanbanSquare, testid: "nav-kanban", module: "lifecycle" },
];

function navItemCls({ isActive }) {
  return `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors border-l-2 ${
    isActive
      ? "text-white border-l-transparent"
      : "text-slate-300 hover:bg-white/5 hover:text-white border-l-transparent"
  }`;
}
function navItemStyle({ isActive }) {
  return isActive ? { backgroundColor: RED, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.05)" } : undefined;
}

export default function Layout({ children }) {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const commercialItems = COMMERCIAL_NAV.filter((i) => hasPerm(i.module, "view"));
  const mfgItems = MFG_NAV.filter((i) => hasPerm(i.module, "view"));
  const showDispatchLink = hasPerm("dispatch", "view");

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Sidebar — Navy */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex w-64 fixed h-screen left-0 top-0 flex-col z-40 text-white"
        style={{ backgroundColor: NAVY, borderRight: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="h-20 px-5 flex items-center border-b border-white/10">
          <Link to="/reports" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white p-1 shadow-sm flex items-center justify-center">
              <img src="/brand/hispl-logo.jpg" alt="HISPL" className="w-full h-full object-contain" data-testid="sidebar-logo" />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-[15px] tracking-wide">HISPL NEXUS</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400">Hydraulics India Services</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {hasPerm("reports", "view") && (
            <NavLink to="/reports" data-testid="nav-reports" className={navItemCls} style={navItemStyle}>
              <BarChart3 className="w-4 h-4" />
              Reports
            </NavLink>
          )}

          {commercialItems.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 px-3 pt-5 pb-2">Commercial</div>
              {commercialItems.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={item.testid} className={navItemCls} style={navItemStyle}>
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
              {showDispatchLink && (
                <NavLink to="/lifecycle?ready_for_dispatch=1" data-testid="nav-dispatch" className={navItemCls} style={navItemStyle}>
                  <Truck className="w-4 h-4" />
                  Dispatch
                </NavLink>
              )}
            </>
          )}

          {mfgItems.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 px-3 pt-5 pb-2">Manufacturing</div>
              {mfgItems.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={item.testid} className={navItemCls} style={navItemStyle}>
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}

          {isAdmin && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 px-3 pt-5 pb-2">Administration</div>
              <NavLink to="/admin/users" data-testid="nav-user-management" className={navItemCls} style={navItemStyle}>
                <User className="w-4 h-4" />
                User &amp; Access
              </NavLink>
              <NavLink to="/admin/import-centre" data-testid="nav-import-centre" className={navItemCls} style={navItemStyle}>
                <Upload className="w-4 h-4" />
                Import Centre
              </NavLink>
              <NavLink to="/admin/system-maintenance" data-testid="nav-system-maintenance" className={navItemCls} style={navItemStyle}>
                <ShieldAlert className="w-4 h-4" />
                System Maintenance
              </NavLink>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 px-3 pt-5 pb-2">Dev Tools</div>
              <NavLink
                to="/cylinders/create"
                data-testid="nav-create-cylinder"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors border-l-2 border-l-transparent ${
                    isActive ? "bg-amber-500/15 text-amber-200" : "text-amber-200/80 hover:bg-white/5 hover:text-amber-200"
                  }`
                }
              >
                <Plus className="w-4 h-4" />
                Create Cylinder
                <span className="ml-auto text-[9px] font-semibold uppercase bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">
                  Dev
                </span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="px-4 py-3 border-t border-white/10">
          <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: GREEN }} />
            <span>HISPL NEXUS v1.0</span>
          </div>
          <div className="mt-1 text-[9px] text-slate-500 uppercase tracking-wider">
            © {new Date().getFullYear()} Hydraulics India Services Private Limited
          </div>
          <div className="mt-1 text-[9px] text-slate-500 italic">
            Engineering Every Cylinder. Tracking Every Step.
          </div>
        </div>
      </aside>

      {/* Top bar */}
      <header
        data-testid="app-topbar"
        className="h-16 bg-white sticky top-0 z-30 flex items-center justify-between px-4 md:px-6 md:ml-64"
        style={{ borderBottom: "1px solid #E5E7EB" }}
      >
        <div className="flex items-center gap-3">
          <div className="md:hidden flex items-center gap-2 font-bold text-slate-900">
            <img src="/brand/hispl-logo.jpg" alt="HISPL" className="w-8 h-8 rounded" />
            HISPL <span style={{ color: RED }}>NEXUS</span>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <span>Manufacturing Execution System</span>
            <span className="text-slate-300">·</span>
            <span className="font-semibold" style={{ color: NAVY }}>
              {user ? `Welcome, ${user.department || user.name}` : ""}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <GlobalSearch />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <div className="w-7 h-7 rounded-full text-white flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: NAVY }}>
                  {(user?.department || user?.name || "U")[0]}
                </div>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-xs font-semibold text-slate-900 uppercase">{user?.department || user?.name}</div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: RED }}>
                    {ROLE_LABELS[user?.role] || user?.role} Department
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold uppercase">{user?.department || user?.name}</div>
                <div className="text-xs text-slate-500 font-normal font-mono">{user?.email}</div>
                <div className="text-[10px] uppercase tracking-wider font-semibold mt-1" style={{ color: RED }}>
                  {ROLE_LABELS[user?.role] || user?.role} Department
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-my-profile" onClick={() => navigate("/profile")}>
                <User className="w-4 h-4 mr-2" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-change-password" onClick={() => navigate("/profile/change-password")}>
                <KeyRound className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={() => logout()} style={{ color: RED }}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="md:ml-64 p-4 md:p-6 bg-white">{children}</main>
    </div>
  );
}
